import React, { useEffect, useState, useRef, useCallback, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { db } from '../../services/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { UserContext } from '../../contexts/UserContext';
import './Shop.css';
import ProductPopup from './ProductPopup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faHeart,
  faUserCircle,
  faShoppingBag,
  faPaperPlane,
  faBookmark,
  faFilter,
  faNewspaper,
  faArrowUp,
  faArrowDown,
  faArrowsUpDown
} from '@fortawesome/free-solid-svg-icons';

/**
 * Shop Component - Enhanced with Product Modal
 */
const Shop = () => {
  // Router hooks
  const location = useLocation();
  const navigate = useNavigate();
  
  // User context
  const { saveItem, isSaved, user } = useContext(UserContext);
  
  // Product data state
  const [products, setProducts] = useState([]);
  const [displayProducts, setDisplayProducts] = useState([]);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [editorialFeaturedProducts, setEditorialFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoverStates, setHoverStates] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [sortOption, setSortOption] = useState('featured');
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [scrollDirection, setScrollDirection] = useState('up');
  const [currentScrollY, setCurrentScrollY] = useState(0);
  const [showBottomNav, setShowBottomNav] = useState(false);

  // Modal state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Editorial attribution state
  const [editorialReferrer, setEditorialReferrer] = useState(null);
  
  // Video references for boomerang effect
  const leftVideoRef = useRef(null);
  const rightVideoRef = useRef(null);
  
  // Search management
  const searchTimeoutRef = useRef(null);
  const lastSearchQueryRef = useRef('');
  const lastSearchCategoryRef = useRef('All');
  
  // Pagination
  const productsPerPage = 24;
  const observer = useRef();
  const loadingMoreRef = useRef(false);
  
  // Debug mode
  const debug = false;
  
  // Fallback image
  const fallbackImage = "/assets-branding/artifex-banner.png";

  // Editorial Products Mapping
  const editorialProductMapping = {
    'tizio-lamp': { editorialId: 'tizio-editorial', editorialTitle: 'California Modernist Lighting' },
    'akari-lights': { editorialId: 'akari-editorial', editorialTitle: 'Noguchi\'s Paper Poetry' },
    'sonos-one': { editorialId: 'sonos-editorial', editorialTitle: 'Invisible Sound Systems' }
  };

  // Open product modal
  const openProductModal = useCallback((product) => {
    setSelectedProduct(product);
    setShowModal(true);
  }, []);

  // Close product modal
  const closeProductModal = useCallback(() => {
    setShowModal(false);
    setTimeout(() => setSelectedProduct(null), 300);
  }, []);

  // Boomerang video effect setup
  const setupBoomerangEffect = useCallback((videoElement) => {
    if (!videoElement) return;
    
    let isReversing = false;
    let cleanupFunctions = [];
    
    const handleTimeUpdate = () => {
      if (isReversing) {
        if (videoElement.currentTime <= 0.1) {
          isReversing = false;
          videoElement.currentTime = 0;
          videoElement.play().catch(console.error);
        }
      }
    };
    
    const handleEnded = () => {
      if (!isReversing) {
        isReversing = true;
        videoElement.currentTime = videoElement.duration - 0.1;
        
        const reverseInterval = setInterval(() => {
          if (videoElement.currentTime <= 0.1) {
            clearInterval(reverseInterval);
            isReversing = false;
            videoElement.currentTime = 0;
            videoElement.play().catch(console.error);
          } else {
            videoElement.currentTime -= 0.033;
          }
        }, 33);
        
        cleanupFunctions.push(() => clearInterval(reverseInterval));
      }
    };
    
    const handleCanPlayThrough = () => {
      videoElement.play().catch(console.error);
    };
    
    const handleLoadedData = () => {
      videoElement.playbackRate = 0.6;
      videoElement.style.imageRendering = 'auto';
      videoElement.style.filter = 'contrast(1.05) saturate(1.05) brightness(1.02)';
      videoElement.play().catch(console.error);
    };
    
    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('ended', handleEnded);
    videoElement.addEventListener('canplaythrough', handleCanPlayThrough);
    videoElement.addEventListener('loadeddata', handleLoadedData);
    
    if (videoElement.readyState >= 3) {
      handleLoadedData();
    }
    
    const cleanup = () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('ended', handleEnded);
      videoElement.removeEventListener('canplaythrough', handleCanPlayThrough);
      videoElement.removeEventListener('loadeddata', handleLoadedData);
      cleanupFunctions.forEach(fn => fn());
    };
    
    cleanupFunctions.push(cleanup);
    return cleanup;
  }, []);

  useEffect(() => {
    return () => {
      if (leftVideoRef.current) {
        leftVideoRef.current.pause();
      }
      if (rightVideoRef.current) {
        rightVideoRef.current.pause();
      }
    };
  }, []);

  // Handle scroll direction for bottom nav
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      if (scrollY > currentScrollY) {
        setScrollDirection('down');
        setShowBottomNav(false);
      } else {
        setScrollDirection('up');
        setShowBottomNav(scrollY > 100);
      }
      setCurrentScrollY(scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentScrollY]);

  // Window resize listener for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track editorial attribution
  const trackEditorialAttribution = useCallback((action, productId, editorialId = null) => {
    try {
      const attribution = {
        action,
        productId,
        editorialId: editorialId || editorialReferrer,
        timestamp: Date.now(),
        userId: user?.uid || 'anonymous',
        sessionId: sessionStorage.getItem('sessionId') || 'unknown'
      };

      const existingData = JSON.parse(localStorage.getItem('editorialAttribution') || '[]');
      existingData.push(attribution);
      localStorage.setItem('editorialAttribution', JSON.stringify(existingData));

      if (debug) {
        console.log('📊 Editorial Attribution Tracked:', attribution);
      }
    } catch (error) {
      console.error('Error tracking editorial attribution:', error);
    }
  }, [editorialReferrer, user, debug]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const editorialRef = urlParams.get('from_editorial');
    const editorialSearchTerms = urlParams.get('editorial_search');
    
    if (editorialRef) {
      setEditorialReferrer(editorialRef);
      trackEditorialAttribution('shop_visit_from_editorial', null, editorialRef);
      
      if (editorialSearchTerms) {
        setSearchQuery(editorialSearchTerms);
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete('from_editorial');
        newUrl.searchParams.delete('editorial_search');
        newUrl.searchParams.set('q', editorialSearchTerms);
        navigate(newUrl.pathname + newUrl.search, { replace: true });
      }
    }
  }, [location.search, navigate, trackEditorialAttribution]);

  const scoreContentAgainstProfile = (item, userProfile) => {
    if (!userProfile?.curationSignature?.signalWeights) {
      return { score: 0.5, passesThreshold: true };
    }

    const signalWeights = userProfile.curationSignature.signalWeights;
    const threshold = userProfile.curationSignature.scoreThreshold || 0.6;
    
    const itemSignals = [
      ...(Array.isArray(item.signals) ? item.signals : []),
      ...(Array.isArray(item.tags) ? item.tags : []),
      ...(Array.isArray(item.hiddenKeywords) ? item.hiddenKeywords : []),
      item.category,
      item.brand || item.vendor_name
    ].filter(Boolean).map(s => s.toLowerCase());

    let totalScore = 0;
    let maxPossibleScore = 0;
    let matchCount = 0;

    Object.entries(signalWeights).forEach(([signal, weight]) => {
      const signalLower = signal.toLowerCase();
      maxPossibleScore += Math.abs(weight);
      
      if (itemSignals.some(itemSignal => itemSignal.includes(signalLower) || signalLower.includes(itemSignal))) {
        totalScore += weight;
        matchCount++;
      }
    });

    const normalizedScore = maxPossibleScore > 0 ? 
      Math.max(0, (totalScore + maxPossibleScore) / (2 * maxPossibleScore)) : 
      0.5;

    return {
      score: normalizedScore,
      passesThreshold: normalizedScore >= threshold,
      matchCount
    };
  };

  const performSearch = useCallback(async (query, category, allProducts) => {
    setIsSearching(true);
    
    let filteredList = allProducts;
    
    if (query && query.trim()) {
      const searchTerms = query.toLowerCase().trim().split(/\s+/).filter(term => term.length > 1);
      
      filteredList = filteredList.filter(product => {
        const searchableFields = [
          product.title || '',
          product.description || '',
          product.brand || product.vendor_name || '',
          product.category || '',
          ...(Array.isArray(product.hiddenKeywords) ? product.hiddenKeywords : []),
          ...(Array.isArray(product.tags) ? product.tags : []),
          ...(Array.isArray(product.signals) ? product.signals : [])
        ];
        
        const searchText = searchableFields.join(' ').toLowerCase();
        
        let keywordScore = 0;
        let matchedTerms = 0;
        
        searchTerms.forEach(term => {
          if (searchText.includes(term)) {
            matchedTerms++;
            
            if ((product.title || '').toLowerCase().includes(term) || 
                (product.brand || product.vendor_name || '').toLowerCase().includes(term)) {
              keywordScore += 10;
            } else {
              keywordScore += 5;
            }
          }
        });
        
        const matchRatio = matchedTerms / searchTerms.length;
        if (matchRatio < 0.6) {
          product.searchScore = 0;
          return false;
        }
        
        const { score: preferenceScore } = user ? 
          scoreContentAgainstProfile(product, user) : 
          { score: 0.5 };
        
        const preferenceMultiplier = 0.5 + (preferenceScore * 1.0);
        product.searchScore = keywordScore * preferenceMultiplier;
        product.preferenceScore = preferenceScore;
        
        return true;
      });
      
      filteredList.sort((a, b) => (b.searchScore || 0) - (a.searchScore || 0));
      
    } else if (category !== 'All') {
      filteredList = filteredList.filter(product => 
        product.category === category
      );
      
      if (user?.curationSignature?.signalWeights && 
          Object.keys(user.curationSignature.signalWeights).length > 0) {
        
        filteredList = filteredList.map(product => {
          const { score: preferenceScore } = scoreContentAgainstProfile(product, user);
          product.preferenceScore = preferenceScore;
          product.searchScore = preferenceScore * 100;
          return product;
        });
        
        filteredList.sort((a, b) => (b.preferenceScore || 0) - (a.preferenceScore || 0));
      }
      
    } else {
      if (user?.curationSignature?.signalWeights && 
          Object.keys(user.curationSignature.signalWeights).length > 0) {
        
        filteredList = filteredList.map(product => {
          const { score: preferenceScore } = scoreContentAgainstProfile(product, user);
          product.preferenceScore = preferenceScore;
          product.searchScore = preferenceScore * 100;
          return product;
        });
        
        filteredList.sort((a, b) => (b.preferenceScore || 0) - (a.preferenceScore || 0));
      }
    }
    
    if (sortOption !== 'featured') {
      switch (sortOption) {
        case 'price-low':
          filteredList.sort((a, b) => {
            const priceA = parseFloat(String(a.price).replace(/[^0-9.-]/g, '')) || 0;
            const priceB = parseFloat(String(b.price).replace(/[^0-9.-]/g, '')) || 0;
            return priceA - priceB;
          });
          break;
        case 'price-high':
          filteredList.sort((a, b) => {
            const priceA = parseFloat(String(a.price).replace(/[^0-9.-]/g, '')) || 0;
            const priceB = parseFloat(String(b.price).replace(/[^0-9.-]/g, '')) || 0;
            return priceB - priceA;
          });
          break;
        case 'newest':
          filteredList.sort((a, b) => {
            const dateA = new Date(a.createdAt || a.dateAdded || a.timestamp || 0);
            const dateB = new Date(b.createdAt || b.dateAdded || b.timestamp || 0);
            return dateB - dateA;
          });
          break;
        default:
          break;
      }
    }
    
    setDisplayProducts(filteredList);
    setVisibleProducts(filteredList.slice(0, productsPerPage));
    setIsSearching(false);
  }, [productsPerPage, user, sortOption]);

  const triggerSearch = useCallback((query, category) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      if (query !== lastSearchQueryRef.current || category !== lastSearchCategoryRef.current) {
        lastSearchQueryRef.current = query;
        lastSearchCategoryRef.current = category;
        performSearch(query, category, products);
      }
    }, 300);
  }, [performSearch, products, sortOption]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('q');
    if (searchParam && searchParam !== searchQuery) {
      setSearchQuery(searchParam);
    }
  }, [location.search, searchQuery]);

  const handleHeaderSearch = useCallback((event) => {
    const searchTerm = event.detail?.searchTerm || '';
    
    if (searchTerm === searchQuery) return;
    
    if (searchTerm.trim() && selectedCategory !== 'All') {
      setSelectedCategory('All');
    }
    
    setSearchQuery(searchTerm);
    
    const newUrl = new URL(window.location);
    if (searchTerm.trim()) {
      newUrl.searchParams.set('q', searchTerm);
    } else {
      newUrl.searchParams.delete('q');
    }
    navigate(newUrl.pathname + newUrl.search, { replace: true });
  }, [navigate, searchQuery, selectedCategory]);

  useEffect(() => {
    window.addEventListener('artifex-search', handleHeaderSearch);
    return () => window.removeEventListener('artifex-search', handleHeaderSearch);
  }, [handleHeaderSearch]);

  const handleProductMouseEnter = useCallback((productId) => {
    setHoverStates(prev => ({ ...prev, [productId]: true }));
  }, []);

  const handleProductMouseLeave = useCallback((productId) => {
    setHoverStates(prev => ({ ...prev, [productId]: false }));
  }, []);

  const handleCategoryChange = useCallback((category) => {
    React.startTransition(() => {
      if (searchQuery) {
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete('q');
        navigate(newUrl.pathname + newUrl.search, { replace: true });
      }
      
      setSearchQuery('');
      setSelectedCategory(category);
      
      if (products.length > 0) {
        performSearch('', category, products);
      }
    });
  }, [searchQuery, navigate, products, performSearch]);

  const toggleFilters = useCallback(() => {
    setShowFilters(prev => !prev);
  }, []);

  const addMoreProductsFn = useCallback(() => {
    if (loadingMoreRef.current || visibleProducts.length >= displayProducts.length) return;
    
    loadingMoreRef.current = true;
    
    setTimeout(() => {
      const currentCount = visibleProducts.length;
      const nextBatch = displayProducts.slice(currentCount, currentCount + productsPerPage);
      setVisibleProducts(prev => [...prev, ...nextBatch]);
      loadingMoreRef.current = false;
    }, 100);
  }, [displayProducts, visibleProducts, productsPerPage]);

  const [shouldLoadMore, setShouldLoadMore] = useState(false);

  useEffect(() => {
    if (shouldLoadMore) {
      addMoreProductsFn();
      setShouldLoadMore(false);
    }
  }, [shouldLoadMore, addMoreProductsFn]);

  const lastProductRef = useCallback(node => {
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && displayProducts.length > visibleProducts.length) {
        setShouldLoadMore(true);
      }
    });
    
    if (node) observer.current.observe(node);
  }, [displayProducts, visibleProducts]);

  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      try {
        throw new Error('Using JSON fallback for faster mobile loading');
        
      } catch (err) {
        console.error('❌ Error loading products from Firebase:', err);
        console.log('🔄 Loading fallback data from shop.json...');
        
        try {
          const response = await fetch('/shop.json');
          const jsonData = await response.json();
          
          console.log(`📁 Loaded ${jsonData.length} items from shop.json fallback`);
          
          const processedJsonItems = jsonData.map(item => {
            const processed = {
              ...item,
              image_url: (item.imageUrl || item.image_url)?.replace(/&/g, '%26'),
              vendor_name: item.brand || item.vendor_name || 'Unknown Brand',
              vendor_url: item.affiliateURL || item.affiliateUrl || item.vendor_url || '#'
            };
            return processed;
          });

          setProducts(processedJsonItems);
          
          const categorySet = new Set(processedJsonItems.map(p => p.category).filter(Boolean));
          setCategories(['All', ...Array.from(categorySet)]);
          
          // Featured products - shuffled
          const featuredList = [...processedJsonItems].sort(() => 0.5 - Math.random()).slice(0, 30);
          setFeaturedProducts(featuredList);
          
          const editorialFeatured = processedJsonItems.filter(product => 
            editorialProductMapping[product.id] || 
            editorialProductMapping[product.title?.toLowerCase().replace(/\s+/g, '-')]
          ).slice(0, 3);
          setEditorialFeaturedProducts(editorialFeatured);
          
          // Shuffle all products for initial display
          const shuffledProducts = [...processedJsonItems].sort(() => 0.5 - Math.random());
          setDisplayProducts(shuffledProducts);
          setVisibleProducts(shuffledProducts.slice(0, productsPerPage));
          
          setIsLoading(false);
          
        } catch (jsonError) {
          console.error('❌ Both Firebase and JSON failed:', jsonError);
          setError('Failed to load products from both Firebase and local data');
          setIsLoading(false);
        }
      }
    };
    
    fetchProducts();
  }, [productsPerPage]);

  useEffect(() => {
    if (products.length > 0) {
      triggerSearch(searchQuery, selectedCategory);
    }
  }, [searchQuery, selectedCategory, products, triggerSearch]);

  useEffect(() => {
    if (products.length > 0 && displayProducts.length > 0) {
      performSearch(searchQuery, selectedCategory, products);
    }
  }, [sortOption]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSave = useCallback((e, product) => {
    e.preventDefault();
    e.stopPropagation();
    if (saveItem) {
      saveItem(product.id);
      trackEditorialAttribution('product_saved', product.id);
      if (debug) console.log('Saved product:', product.title || product.id);
    }
  }, [saveItem, debug, trackEditorialAttribution]);

  if (isLoading && !visibleProducts.length) {
    return (
      <div className="shop-loading" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '34vh',
        gap: '1rem'
      }}>
        <div className="loading-spinner" style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #0d47a1',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p>Loading shop products...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shop-error" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '34vh',
        gap: '1rem',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <h2>Error loading products</h2>
        <p style={{ color: '#666' }}>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#0d47a1',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
  <>
    {/* Product Detail Modal */}
    {showModal && selectedProduct && (
      <ProductPopup
        product={selectedProduct}
        onClose={closeProductModal}
        onSave={handleSave}
        isSaved={isSaved}
        trackAttribution={trackEditorialAttribution}
        fallbackImage={fallbackImage}
      />
    )}

    {/* Top navigation menu */}
    {windowWidth >= 576 && (
        <div style={{ 
          marginTop: '60px',
          width: '100%', 
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'white',
          borderBottom: '1px solid #eee',
          padding: '0.5rem 0'
        }}>
          <div style={{ 
            maxWidth: '600px',
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Link to="/discover" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              padding: '0.5rem 1rem',
              background: 'transparent',
              color: '#666',
              textDecoration: 'none',
              borderRadius: '6px',
              minWidth: '80px'
            }}>
              <FontAwesomeIcon icon={faHome} style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }} />
              <span style={{ fontSize: '0.8rem' }}>Discover</span>
            </Link>

            <Link to="/signals" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              padding: '0.5rem 1rem',
              background: 'transparent',
              color: '#666',
              textDecoration: 'none',
              borderRadius: '6px',
              minWidth: '80px'
            }}>
              <FontAwesomeIcon icon={faShoppingBag} style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }} />
              <span style={{ fontSize: '0.8rem' }}>Signals</span>
            </Link>
            
            <Link to="/shop" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              padding: '0.5rem 1rem',
              background: '#0d47a1',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '6px',
              minWidth: '80px'
            }}>
              <FontAwesomeIcon icon={faShoppingBag} style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }} />
              <span style={{ fontSize: '0.8rem' }}>Shop</span>
            </Link>
          </div>
        </div>
      )}

      {/* Editorial referrer banner */}
      {editorialReferrer && (
        <div style={{
          background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
          padding: '0.75rem 1rem',
          textAlign: 'center',
          borderBottom: '1px solid #90caf9',
          marginTop: windowWidth >= 576 ? '0' : '60px'
        }}>
          <FontAwesomeIcon icon={faNewspaper} style={{ marginRight: '0.5rem', color: '#0d47a1' }} />
          <span style={{ fontSize: '0.9rem', color: '#0d47a1' }}>
            Products featured in our latest editorial
          </span>
        </div>
      )}
      
      {/* Mobile filters drawer */}
      {windowWidth < 1300 && showFilters && (
        <div style={{
          position: 'fixed',
          top: editorialReferrer ? '120px' : (windowWidth >= 576 ? '100px' : '60px'),
          left: 0,
          right: 0,
          background: 'white',
          zIndex: 900,
          padding: '1rem',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          transition: 'transform 0.3s ease',
          transform: showFilters ? 'translateY(0)' : 'translateY(-100%)'
        }}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Categories</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
            {categories.map((category, index) => (
              <button 
                key={index}
                onClick={() => {
                  handleCategoryChange(category);
                  setShowFilters(false);
                }}
                style={{
                  padding: '0.5rem 1rem',
                  background: selectedCategory === category ? '#0d47a1' : '#f5f5f5',
                  color: selectedCategory === category ? 'white' : '#333',
                  border: 'none',
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                  cursor: 'pointer'
                }}
              >
                {category}
              </button>
            ))}
          </div>
          
          <button 
            onClick={() => setShowFilters(false)}
            style={{
              display: 'block',
              width: '100%',
              padding: '0.5rem',
              background: '#f5f5f5',
              border: 'none',
              borderRadius: '4px',
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}
          >
            Close Filters
          </button>
        </div>
      )}
      
      {!editorialReferrer && windowWidth < 576 && <div style={{ marginTop: '60px' }}></div>}
      
      {/* Main content container */}
      <div className="shop-container" style={{ 
        paddingTop: windowWidth >= 576 ? '20px' : '20px',
        opacity: showModal ? 0.3 : 1,
        transition: 'opacity 0.3s ease'
      }}>
        {/* Left categories sidebar */}
        {windowWidth >= 1300 && (
          <>
            <div className="shop-left-video-strip">
              <video
                ref={leftVideoRef}
                src="/videos/sidebar-column.mov"
                muted
                loop={false}
                playsInline
                preload="metadata"
                autoPlay
                onLoadedData={() => {
                  if (leftVideoRef.current) {
                    setupBoomerangEffect(leftVideoRef.current);
                  }
                }}
              />
            </div>
            
            <div className="shop-left-sidebar">
              <div className="sidebar-section" style={{
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                padding: '0.2rem 1rem 1rem',
                marginLeft: '10px',
                marginRight: '10px',
                marginTop: '10px',
                marginBottom: '20px',
                minHeight: '650px'
              }}>
                <h3>Categories</h3>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '0.5rem',
                  maxHeight: '585px',
                  overflowY: 'auto',
                  marginTop: '0rem',
                  marginBottom: '1rem'
                }}>
                  {categories.map((category, index) => (
                    <button 
                      key={index}
                      onClick={() => handleCategoryChange(category)}
                      className={selectedCategory === category ? 'selected' : ''}
                    >
                      {category}
                    </button>
                  ))}
                </div>

                <div style={{
                  fontSize: '11px',
                  color: '#666',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  padding: '0.5rem',
                  marginTop: '0.5rem'
                }}>
                  Scroll for more
                </div>
              </div>
            </div>
          </>
        )}

        {/* Fixed Bottom Navigation - Visible on scroll up */}
        <div style={{
          position: 'fixed',
          bottom: showBottomNav ? '20px' : '-80px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '1rem',
          padding: '12px 24px',
          background: 'rgba(255, 255, 255, 0.25)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderRadius: '50px',
          border: '1px solid rgba(255, 255, 255, 0.4)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 2px 4px rgba(255, 255, 255, 0.5)',
          transition: 'bottom 0.3s ease',
          zIndex: 1000
        }}>
            
              <Link
                to="/saved"
                title="Saved items"
                style={{
                  padding: 0,
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(20px) saturate(200%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(200%)',
                  color: '#666',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), inset 0 2px 4px rgba(255, 255, 255, 0.6), inset 0 -2px 4px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => {
                  if (windowWidth >= 576) {
                     
                    e.currentTarget.style.backdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.6), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
                  }
                }}
                onMouseLeave={(e) => {
                   
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.backdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.5), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
                }}
              >
                <FontAwesomeIcon icon={faHeart} style={{ fontSize: '16px' }} />
              </Link>

              {windowWidth < 1300 && (
                <button
                  onClick={toggleFilters}
                  title="Filters"
                  style={{
                    padding: 0,
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(20px) saturate(200%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(200%)',
                    color: '#666',
                    border: '1px solid rgba(255, 255, 255, 0.5)',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '40px',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), inset 0 2px 4px rgba(255, 255, 255, 0.6), inset 0 -2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    if (windowWidth >= 576) {
                       
                      e.currentTarget.style.backdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.6), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                     
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.backdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.5), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
                  }}
                >
                  <FontAwesomeIcon icon={faFilter} style={{ fontSize: '16px' }} />
                </button>
              )}

              <div style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  title={`Sort: ${sortOption === 'featured' ? 'Featured' : sortOption === 'newest' ? 'Newest' : sortOption === 'price-low' ? 'Price: Low to High' : 'Price: High to Low'}`}
                  style={{
                    padding: 0,
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(20px) saturate(200%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(200%)',
                    color: '#666',
                    border: '1px solid rgba(255, 255, 255, 0.5)',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '40px',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), inset 0 2px 4px rgba(255, 255, 255, 0.6), inset 0 -2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                  onClick={() => {
                    const select = document.getElementById('sort-select');
                    if (select) select.focus();
                  }}
                  onMouseEnter={(e) => {
                    if (windowWidth >= 576) {
                       
                      e.currentTarget.style.backdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.6), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                     
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.backdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.5), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
                  }}
                >
                  <FontAwesomeIcon icon={faArrowsUpDown} style={{ fontSize: '16px' }} />
                </button>
                <select
                  id="sort-select"
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '40px',
                    height: '40px',
                    opacity: 0,
                    cursor: 'pointer',
                    borderRadius: '50%'
                  }}
                >
                  <option value="featured">Featured</option>
                  <option value="newest">Newest</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>
              </div>
        </div>

        {/* Main grid container */}
        <div className={`shop-main ${windowWidth < 1300 ? 'mobile-layout' : ''}`}>
          {visibleProducts.length === 0 && !isLoading && !isSearching && (
            <div style={{
              padding: '3rem 1rem',
              textAlign: 'center',
              background: '#f9f9f9',
              borderRadius: '8px'
            }}>
              <FontAwesomeIcon 
                icon={faShoppingBag} 
                style={{ fontSize: '2.5rem', color: '#ccc', marginBottom: '1rem' }}
              />
              <h3 style={{ margin: '0 0 0.5rem', color: '#555' }}>No products found</h3>
              <p style={{ margin: '0 0 1rem', color: '#777' }}>
                {searchQuery ? `No products match "${searchQuery}". Try different search terms.` : 'Try adjusting your filters.'}
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('All');
                  const newUrl = new URL(window.location);
                  newUrl.searchParams.delete('q');
                  navigate(newUrl.pathname + newUrl.search, { replace: true });
                }}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#0d47a1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Reset Filters
              </button>
            </div>
          )}
      
          <div className="shop-grid">
            {visibleProducts.map((product, index) => {
              const editorialInfo = editorialProductMapping[product.id] || 
                                  editorialProductMapping[product.title?.toLowerCase().replace(/\s+/g, '-')];
          
              return (
                <div
                  key={`${product.id}-${index}`}
                  className="shop-card fade-in"
                  ref={index === visibleProducts.length - 1 ? lastProductRef : null}
                >
                  <div
                    className="card-image-container"
                    onClick={() => openProductModal(product)}
                    style={{
                      width: '100%',
                      height: '220px',
                      position: 'relative',
                      overflow: 'hidden',
                      cursor: 'pointer'
                    }}
                  >
                    {/* Primary Image */}
                    <img
                      src={product.images?.[0] || product.image_url || product.imageUrl}
                      alt={product.title || 'Product'}
                      className="card-image"
                      onError={(e) => {
                        e.target.src = fallbackImage;
                      }}
                    />

                    {/* Secondary Image - Crossfade on Hover */}
                    {(product.images && product.images.length > 1) && (
                      <img
                        src={product.images[1]}
                        alt={`${product.title} alternate view` || 'Product alternate'}
                        className="card-image-secondary"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}

                    {/* Premium Hover Overlay - Shop Now Button */}
                    <div className="card-hover-overlay">
                      {product.description && (
                        <div className="hover-overlay-description">
                          {product.description}
                        </div>
                      )}
                      <button
                        className="hover-shop-now-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openProductModal(product);
                        }}
                      >
                        Shop Now
                      </button>
                    </div>

                    {editorialInfo && (
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        background: '#0d47a1',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <FontAwesomeIcon icon={faNewspaper} style={{ fontSize: '0.6rem' }} />
                        FEATURED
                      </div>
                    )}

                    {debug && (product.searchScore || product.preferenceScore) && (
                      <div style={{
                        position: 'absolute',
                        top: editorialInfo ? '40px' : '8px',
                        left: '8px',
                        background: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: 'bold'
                      }}>
                        {product.searchScore ? product.searchScore.toFixed(0) : product.preferenceScore.toFixed(2)}
                      </div>
                    )}

                    <button
                      onClick={(e) => handleSave(e, product)}
                      className="action-button-round"
                      title="Save"
                      style={{
                        position: 'absolute',
                        top: '0.75rem',
                        right: '0.75rem'
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faBookmark}
                        style={{ color: isSaved && isSaved(product.id) ? '#000080' : '#666' }}
                      />
                    </button>
                  </div>

                  <div className="card-details">
                    <div className="card-brand">
                      {(product.vendor_name || product.brand || 'BRAND').toUpperCase()}
                    </div>
                    <div className="card-separator-line"></div>
                    <h3 className="card-title-permanent">
                      {product.title || 'Untitled Product'}
                    </h3>
                    <div className="card-separator-dots"></div>
                    <div className="card-price">
                      ${product.price}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right sidebar */}
        {windowWidth >= 1300 && (
          <>
            <div className="shop-right-video-strip">
              <video
                ref={rightVideoRef}
                src="/videos/sidebar-column.mov"
                muted
                loop={false}
                playsInline
                preload="auto"
                autoPlay
                onLoadedData={() => {
                  if (rightVideoRef.current) {
                    setupBoomerangEffect(rightVideoRef.current);
                  }
                }}
              />
            </div>

            <div className="shop-right-sidebar">
              {editorialFeaturedProducts.length > 0 && (
                <div className="sidebar-section" style={{
                  background: 'rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div className="section-header">
                    <h3 style={{display: 'flex', alignItems: 'center', gap: '0.25rem', marginBottom: '0.5rem'}}>
                      <FontAwesomeIcon icon={faNewspaper} style={{ color: '#0d47a1' }} />
                      Featured in Editorial
                    </h3>
                    <Link to="/editorials" className="see-all-link">
                      See All
                    </Link>
                  </div>
        
                  <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                    {editorialFeaturedProducts.map(product => {
                      const editorialInfo = editorialProductMapping[product.id] || 
                                          editorialProductMapping[product.title?.toLowerCase().replace(/\s+/g, '-')];
            
                      return (
                        <div 
                          key={product.id} 
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            cursor: 'pointer'
                          }}
                          onClick={() => openProductModal(product)}
                        >
                          <div style={{
                            flexShrink: 0,
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            overflow: 'hidden'
                          }}>
                            <img 
                              src={product.image_url || product.imageUrl} 
                              alt={product.title || 'Product'} 
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              onError={(e) => { e.target.src = fallbackImage }}
                            />
                          </div>
                          <div style={{flexGrow: 1, overflow: 'hidden'}}>
                            <div style={{
                              display: 'block',
                              color: '#111',
                              fontWeight: 600,
                              fontSize: '13px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {product.title || 'Untitled Product'}
                            </div>
                            <span style={{
                              display: 'block',
                              fontSize: '11px',
                              color: '#666',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              ${product.price} - {product.vendor_name || product.brand || 'Unknown vendor'}
                            </span>
                            {editorialInfo && (
                              <span style={{
                                display: 'block',
                                fontSize: '11px',
                                color: '#0d47a1',
                                fontStyle: 'italic'
                              }}>
                                From: {editorialInfo.editorialTitle}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
    
              <div className="sidebar-section" style={{
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                padding: '1rem',
                marginLeft: '10px',
                marginRight: '10px',
                marginTop: '10px',
                marginBottom: '1rem',
                paddingBottom: '1rem'
              }}>
                <div className="section-header">
                  <h3>Recommended</h3>
                </div>
      
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '.2rem',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  marginTop: '-1rem',
                  marginBottom: '1rem'
                }}>
                  {featuredProducts.map((product, index) => (
                    <button 
                      key={index}
                      onClick={() => {
                        const searchTerm = product.brand || product.vendor_name || product.title?.substring(0, 20);
                        setSearchQuery(searchTerm);
                        const newUrl = new URL(window.location);
                        newUrl.searchParams.set('q', searchTerm);
                        navigate(newUrl.pathname + newUrl.search, { replace: true });
                      }}
                    >
                      {product.brand || product.vendor_name || (product.title?.substring(0, 20) + (product.title?.length > 20 ? '...' : ''))}
                    </button>
                  ))}
                </div>

                <div style={{
                  fontSize: '11px',
                  color: '#666',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  padding: '0.5rem',
                  marginTop: '0.5rem'
                }}>
                  Scroll for more
                </div>
              </div>
    
              <div className="sidebar-footer" style={{
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                padding: '1rem',
                marginLeft: '10px',
                marginRight: '10px',
                marginBottom: '20px'
              }}>
                <div className="footer-links" style={{
                  transform: 'translateX(20px)'
                }}>
                  <span>About</span>
                  <span>Help</span>
                  <span>Terms</span>
                  <span>Privacy</span>
                </div>
                <div className="copyright" style={{
                  transform: 'translateX(15px)'
                }}>
                  © Artifex {new Date().getFullYear()}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Shop;